import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  fetchLeasingSchedule,
  importLeasingSchedulePdf,
  markLeasingInstallmentManualApi,
  previewLeasingSchedulePdf,
  patchLeasingInstallment,
  fetchLeasingInvoiceCandidatesApi,
  regenerateLeasingSchedule,
  reopenLeasingInstallmentAsPartialApi,
  resolveLeasingInvoiceByDocNumApi,
  setLeasingInstallmentInvoiceDocEntryApi,
  unmarkLeasingInstallmentManualApi,
  type LeasingInstallment,
  type LeasingPdfPreview,
  type LeasingScheduleEditableField,
  type LeasingScheduleResponse,
} from "../api";
import { formatDateEs, formatMoney } from "../lib/grouping";

/**
 * Vista del cuadro de amortización de un leasing (Fase 2).
 *
 * - Si aún no se ha generado, botón "Generar cuadro" que llama al endpoint
 *   regenerate con los parámetros del contrato del leasing.
 * - Si el cuadro existe y los parámetros del contrato han cambiado desde su
 *   generación, aviso "cuadro obsoleto" con botón regenerar (conserva las
 *   cuotas pagadas por índice).
 * - Cada celda editable (Fecha, Base, Interés, Amortizado, Envío, Total)
 *   se puede editar clicando. Al confirmar (Enter/blur) se hace PATCH y el
 *   backend recalcula en CASCADA las cuotas posteriores usando la tasa del
 *   contrato para intereses.
 * - IVA y capital pendiente NO son editables (derivan).
 * - La cuota residual N+1 solo permite editar la fecha.
 */
export function LeasingSchedule() {
  const { bankCode, contractNumber } = useParams<{ bankCode: string; contractNumber: string }>();
  const [data, setData] = useState<LeasingScheduleResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const pdfInputRef = useRef<HTMLInputElement | null>(null);
  const [pdfPreview, setPdfPreview] = useState<{ file: File; preview: LeasingPdfPreview } | null>(null);

  const load = async () => {
    if (!bankCode || !contractNumber) return;
    setLoading(true);
    setError(null);
    try {
      const r = await fetchLeasingSchedule(bankCode, contractNumber);
      setData(r);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankCode, contractNumber]);

  const doRegenerate = async () => {
    if (!bankCode || !contractNumber) return;
    if (data && data.installments.length > 0) {
      if (!confirm("¿Regenerar el cuadro desde los parámetros del contrato? Las cuotas ya pagadas se conservan por número.")) return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await regenerateLeasingSchedule(bankCode, contractNumber);
      alert(`✓ Cuadro generado con ${r.installmentCount} cuotas.`);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const doPreviewPdf = async (file: File) => {
    if (!bankCode || !contractNumber) return;
    setBusy(true);
    setError(null);
    try {
      const preview = await previewLeasingSchedulePdf(bankCode, contractNumber, file);
      setPdfPreview({ file, preview });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
      if (pdfInputRef.current) pdfInputRef.current.value = "";
    }
  };

  const confirmImportPdf = async () => {
    if (!bankCode || !contractNumber || !pdfPreview) return;
    if (data && data.installments.length > 0) {
      if (!confirm(`Reemplazar el cuadro actual (${data.installments.length} cuotas) por las ${pdfPreview.preview.rowsCount} del PDF? Las cuotas ya contabilizadas se preservan por número.`)) return;
    }
    setBusy(true);
    setError(null);
    try {
      const r = await importLeasingSchedulePdf(bankCode, contractNumber, pdfPreview.file);
      alert(`✓ Cuadro importado del PDF: ${r.installmentCount} cuotas.`);
      setPdfPreview(null);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const onEditCell = async (
    atIndex: number,
    field: LeasingScheduleEditableField,
    value: number | string,
  ) => {
    if (!bankCode || !contractNumber) return;
    setBusy(true);
    setError(null);
    try {
      await patchLeasingInstallment(bankCode, contractNumber, atIndex, field, value);
      await load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!bankCode || !contractNumber) {
    return <p className="error">Faltan bankCode / contractNumber en la URL.</p>;
  }

  const currency = data?.leasing.currency || "EUR";

  // Snapshot desactualizado: params actuales del leasing != los que
  // generaron el cuadro.
  const snapshotOutdated = (() => {
    if (!data || !data.paramsSnapshot) return false;
    const l = data.leasing;
    const s = data.paramsSnapshot;
    return (
      l.startDate !== s.startDate ||
      l.principal !== s.principal ||
      l.installmentCount !== s.installmentCount ||
      l.interestRateAnnual !== s.interestRateAnnual ||
      l.vatRatePercent !== s.vatRatePercent ||
      (l.residualValue ?? 0) !== s.residualValue ||
      (l.postalFeeMonthly ?? 0) !== s.postalFeeMonthly
    );
  })();

  const totals = data
    ? data.installments.reduce(
        (acc, r) => ({
          base: acc.base + r.base,
          interest: acc.interest + r.interest,
          amortized: acc.amortized + r.amortized,
          vat: acc.vat + r.vat,
          postalFee: acc.postalFee + (r.postalFee ?? 0),
          total: acc.total + r.total,
          totalPayment: acc.totalPayment + (r.totalPayment ?? r.total),
        }),
        { base: 0, interest: 0, amortized: 0, vat: 0, postalFee: 0, total: 0, totalPayment: 0 },
      )
    : null;

  return (
    <section className="card">
      <div className="card-head">
        <div>
          <h2>Cuadro leasing · {data?.leasing.alias || contractNumber}</h2>
          <p className="meta">
            <Link to="/leasings">← Leasings</Link>
            {"  ·  "}Entidad <code>{bankCode}</code>
            {"  ·  "}Contrato <code>{contractNumber}</code>
            {data?.leasing.supplier && (
              <>
                {"  ·  "}Proveedor {data.leasing.supplier}
                {data.leasing.supplierCardCode && <> · <code>{data.leasing.supplierCardCode}</code></>}
              </>
            )}
          </p>
        </div>
        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button type="button" onClick={load} disabled={busy}>Recargar</button>
          <button type="button" onClick={doRegenerate} disabled={busy}>
            {busy ? "…" : (data && data.installments.length > 0 ? "↻ Regenerar" : "Generar cuadro")}
          </button>
          <button
            type="button"
            onClick={() => pdfInputRef.current?.click()}
            disabled={busy}
            title="Sube el PDF del cuadro del banco. Se extraerá el texto y se cargarán las filas tal cual."
          >
            📄 Subir PDF del cuadro
          </button>
          <input
            ref={pdfInputRef}
            type="file"
            accept="application/pdf"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void doPreviewPdf(f);
            }}
          />
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p>Cargando…</p>}

      {data && (
        <>
          <div className="meta" style={{ marginTop: 4, fontSize: "0.85em" }}>
            {data.leasing.startDate && <>Inicio <strong>{formatDateEs(data.leasing.startDate)}</strong>{"  ·  "}</>}
            {data.leasing.principal !== null && <>Capital <strong>{formatMoney(data.leasing.principal, currency)}</strong>{"  ·  "}</>}
            {data.leasing.installmentCount !== null && <>{data.leasing.installmentCount} cuotas{"  ·  "}</>}
            {data.leasing.interestRateAnnual !== null && <>Tipo <strong>{data.leasing.interestRateAnnual}%</strong> anual{"  ·  "}</>}
            {data.leasing.vatRatePercent !== null && <>IVA <strong>{data.leasing.vatRatePercent}%</strong>{"  ·  "}</>}
            {data.leasing.residualValue !== null && data.leasing.residualValue > 0 && <>Residual <strong>{formatMoney(data.leasing.residualValue, currency)}</strong>{"  ·  "}</>}
            {data.leasing.postalFeeMonthly !== null && data.leasing.postalFeeMonthly > 0 && <>Envío/mes <strong>{formatMoney(data.leasing.postalFeeMonthly, currency)}</strong></>}
          </div>
          {data.leasing.interestRateTranches && data.leasing.interestRateTranches.length > 0 && (
            <div className="meta" style={{ marginTop: 4, fontSize: "0.85em" }}>
              Tramos:&nbsp;
              {data.leasing.interestRateTranches.map((t, i) => (
                <span key={i} style={{ marginRight: 8 }}>
                  desde cuota <strong>{t.fromInstallment}</strong> → <strong>{t.annualRate}%</strong>
                </span>
              ))}
            </div>
          )}

          {snapshotOutdated && (
            <p className="warning" style={{ marginTop: 8 }}>
              ⚠️ Los parámetros del contrato han cambiado desde la última generación.
              Pulsa <strong>↻ Regenerar</strong> para recalcular todo el cuadro con
              los valores actuales. Las cuotas ya contabilizadas se conservan.
            </p>
          )}

          {(() => {
            // Aviso factura pendiente >30d: cuotas en status "partial" con
            // fecha original de la cuota más de 30 días atrás. Indica que el
            // cargo residual no ha llegado y el acreedor sigue con saldo.
            const today = new Date();
            const stale = data.installments.filter((i) => {
              if (i.status !== "partial") return false;
              const [y, mo, d] = i.date.slice(0, 10).split("-").map(Number);
              if (!y || !mo || !d) return false;
              const instDate = new Date(Date.UTC(y, mo - 1, d));
              const daysAgo = Math.floor(
                (today.getTime() - instDate.getTime()) / (1000 * 60 * 60 * 24),
              );
              return daysAgo > 30;
            });
            if (stale.length === 0) return null;
            return (
              <p className="warning" style={{ marginTop: 8 }}>
                ⚠️ Hay {stale.length}{" "}
                {stale.length === 1 ? "cuota parcialmente pagada" : "cuotas parcialmente pagadas"}{" "}
                sin cerrar hace más de 30 días (
                {stale.map((s) => `#${s.index}`).join(", ")}). El cargo residual
                nunca llegó y las facturas asociadas (DocEntry{" "}
                {stale
                  .map((s) => s.invoiceDocEntry ?? "?")
                  .filter((v, i, arr) => arr.indexOf(v) === i)
                  .join(", ")}
                ) siguen con saldo pendiente en el acreedor. Revisa manualmente
                en SAP o desde el retry-reconcile.
              </p>
            );
          })()}

          {data.installments.length === 0 && (
            <div style={{ marginTop: 12, padding: 12, background: "rgba(80,160,255,0.06)", borderRadius: 6 }}>
              <p className="meta" style={{ margin: 0 }}>
                No hay cuadro generado. Rellena los parámetros del contrato en la ficha del
                leasing y pulsa <strong>Generar cuadro</strong>.
              </p>
            </div>
          )}

          {data.installments.length > 0 && (
            <table className="data" style={{ marginTop: 8 }}>
              <thead>
                <tr>
                  <th className="num">Nº</th>
                  <th>Fecha</th>
                  <th className="num">Base</th>
                  <th className="num">Interés</th>
                  <th className="num">Amortizado</th>
                  <th className="num" title="IVA sobre (base + envío)">IVA</th>
                  <th className="num" title="Envío por correo (base, sin IVA). Su IVA se suma en la columna IVA.">Envío</th>
                  <th className="num" title="Total factura de la cuota = base + IVA. NO incluye envío.">Total</th>
                  <th className="num" title="Total pago = base + envío + IVA. Es el importe que efectivamente se carga en cuenta.">Total pago</th>
                  <th className="num">Pdte capital</th>
                  <th>Pago</th>
                </tr>
              </thead>
              <tbody>
                {data.installments.map((r) => (
                  <ScheduleRow
                    key={r.index}
                    row={r}
                    currency={currency}
                    onEditCell={onEditCell}
                    onToggleManualMark={async () => {
                      if (!bankCode || !contractNumber) return;
                      try {
                        if (r.paidAt && r.manuallyMarked) {
                          await unmarkLeasingInstallmentManualApi(bankCode, contractNumber, r.index);
                        } else if (!r.paidAt) {
                          const today = new Date().toISOString().slice(0, 10);
                          const paidAt = prompt(
                            "Fecha de contabilización manual (YYYY-MM-DD):",
                            r.date?.slice(0, 10) || today,
                          );
                          if (!paidAt || !/^\d{4}-\d{2}-\d{2}$/.test(paidAt)) return;
                          await markLeasingInstallmentManualApi(bankCode, contractNumber, r.index, paidAt);
                        }
                        await load();
                      } catch (e: unknown) {
                        alert(e instanceof Error ? e.message : String(e));
                      }
                    }}
                    onReopenAsPartial={async () => {
                      if (!bankCode || !contractNumber) return;
                      const total = r.totalPayment ?? r.total;
                      // Paso 1: intenta autocompletar buscando facturas
                      // candidatas en SAP. Si hay 0 → fallback manual (prompts).
                      // Si hay 1 → confirma directamente. Si hay N → el usuario elige.
                      let paidAmount = 0;
                      let invoiceDocEntry = 0;
                      let usedLookup = false;
                      try {
                        const candidates = await fetchLeasingInvoiceCandidatesApi(
                          bankCode,
                          contractNumber,
                          r.index,
                        );
                        if (candidates.length > 0) {
                          usedLookup = true;
                          let chosenIdx = 0;
                          if (candidates.length > 1) {
                            const list = candidates
                              .map(
                                (c, i) =>
                                  `${i + 1}. DocEntry ${c.docEntry} · Nº ${c.docNum} · ${c.docDate} · Total ${c.docTotal.toFixed(2)}€ · Pagado ${c.paidToDate.toFixed(2)}€ · Residuo ${c.residualExpected.toFixed(2)}€`,
                              )
                              .join("\n");
                            const pick = prompt(
                              `Hay ${candidates.length} facturas candidatas en SAP para la cuota #${r.index}. Elige por número:\n\n${list}\n\nEscribe 1-${candidates.length}:`,
                              "1",
                            );
                            if (!pick) return;
                            const n = parseInt(pick, 10);
                            if (!Number.isFinite(n) || n < 1 || n > candidates.length) {
                              alert("Selección inválida.");
                              return;
                            }
                            chosenIdx = n - 1;
                          }
                          const chosen = candidates[chosenIdx]!;
                          invoiceDocEntry = chosen.docEntry;
                          paidAmount = chosen.paidToDate;
                          if (paidAmount <= 0 || paidAmount >= total) {
                            alert(
                              `La factura DocEntry ${invoiceDocEntry} en SAP tiene PaidToDate=${paidAmount.toFixed(2)}€ (total ${chosen.docTotal.toFixed(2)}€). No es un pago parcial válido (debe estar entre 0 y ${total.toFixed(2)}€). Comprueba en SAP.`,
                            );
                            return;
                          }
                        }
                      } catch (e: unknown) {
                        console.warn("Lookup SAP falló, cayendo a modo manual:", e);
                      }
                      if (!usedLookup) {
                        const paidStr = prompt(
                          `No se encontraron facturas candidatas en SAP.\n\nReabrir cuota #${r.index} manualmente.\nImporte YA pagado (< ${total.toFixed(2)}€):`,
                          "",
                        );
                        if (!paidStr) return;
                        paidAmount = Number(paidStr.replace(",", "."));
                        if (!Number.isFinite(paidAmount) || paidAmount <= 0 || paidAmount >= total) {
                          alert(`Importe inválido. Debe ser un número entre 0 y ${total.toFixed(2)}€.`);
                          return;
                        }
                        const docStr = prompt(
                          `DocEntry de la PurchaseInvoice ya emitida en SAP:`,
                          "",
                        );
                        if (!docStr) return;
                        invoiceDocEntry = Number(docStr);
                        if (!Number.isFinite(invoiceDocEntry) || invoiceDocEntry <= 0) {
                          alert("DocEntry inválido — debe ser un número entero positivo.");
                          return;
                        }
                      }
                      if (
                        !confirm(
                          `¿Reabrir cuota #${r.index} como PARCIAL?\n\n` +
                            `- paidAmount: ${paidAmount.toFixed(2)}€ ${usedLookup ? "(desde SAP PaidToDate)" : "(manual)"}\n` +
                            `- residual esperado: ${(total - paidAmount).toFixed(2)}€\n` +
                            `- invoiceDocEntry: ${invoiceDocEntry}${usedLookup ? " (desde SAP)" : " (manual)"}\n\n` +
                            `La cuota dejará de estar como pagada. El próximo cargo del banco por ${(total - paidAmount).toFixed(2)}€ se contabilizará como RESIDUAL y cerrará la cuota.`,
                        )
                      )
                        return;
                      try {
                        await reopenLeasingInstallmentAsPartialApi(
                          bankCode,
                          contractNumber,
                          r.index,
                          paidAmount,
                          invoiceDocEntry,
                        );
                        await load();
                      } catch (e: unknown) {
                        alert(e instanceof Error ? e.message : String(e));
                      }
                    }}
                    onEditInvoiceDocEntry={async () => {
                      if (!bankCode || !contractNumber) return;
                      // Buscamos facturas del proveedor cerca de la cuota
                      // (ventana ±60d, sin filtro de total).
                      let candidates: import("../api").LeasingInvoiceCandidate[] = [];
                      try {
                        candidates = await fetchLeasingInvoiceCandidatesApi(
                          bankCode,
                          contractNumber,
                          r.index,
                        );
                      } catch (e: unknown) {
                        console.warn("Lookup SAP falló:", e);
                      }
                      const currentPos =
                        r.invoiceDocEntry != null
                          ? candidates.findIndex((c) => c.docEntry === r.invoiceDocEntry) + 1
                          : 0;
                      const listaCandidatos =
                        candidates.length > 0
                          ? candidates
                              .map(
                                (c, i) =>
                                  `${i + 1}. Nº factura ${c.docNum} · ${c.docDate} · Total ${c.docTotal.toFixed(2)}€ · Pagado ${c.paidToDate.toFixed(2)}€ · Pendiente ${c.residualExpected.toFixed(2)}€`,
                              )
                              .join("\n")
                          : "(SAP no devolvió facturas del proveedor en ±60 días)";
                      const prompt1 = prompt(
                        `Vincular factura a la cuota #${r.index} (${r.date}).\n\n` +
                          `Valor actual: ${r.invoiceDocEntry != null ? `ID interno ${r.invoiceDocEntry}` : "(sin factura)"}\n\n` +
                          `Facturas del proveedor cercanas:\n${listaCandidatos}\n\n` +
                          `Opciones:\n` +
                          `  · ${candidates.length > 0 ? `1-${candidates.length} = elegir de la lista` : "(no hay lista)"}\n` +
                          `  · F = buscar por Nº de factura (el visible)\n` +
                          `  · D = introducir el DocEntry (ID interno) directamente\n` +
                          `  · X = dejar la cuota SIN factura vinculada\n\n` +
                          `Tu elección:`,
                        candidates.length > 0 && currentPos > 0 ? String(currentPos) : (candidates.length > 0 ? "1" : "F"),
                      );
                      if (prompt1 === null) return;
                      const t = prompt1.trim().toUpperCase();
                      let newDoc: number | null;
                      if (t === "X") {
                        newDoc = null;
                      } else if (t === "D") {
                        const de = prompt(
                          `DocEntry (ID interno de la factura en SAP, entero > 0):`,
                          r.invoiceDocEntry != null ? String(r.invoiceDocEntry) : "",
                        );
                        if (!de) return;
                        const n = Number(de.trim());
                        if (!Number.isFinite(n) || n <= 0) {
                          alert("DocEntry inválido.");
                          return;
                        }
                        newDoc = Math.floor(n);
                      } else if (t === "F") {
                        const docNumStr = prompt(
                          `Escribe el Nº de factura (el que sale impreso en la factura del proveedor):`,
                          "",
                        );
                        if (!docNumStr) return;
                        const docNum = Number(docNumStr.trim());
                        if (!Number.isFinite(docNum) || docNum <= 0) {
                          alert("Nº de factura inválido.");
                          return;
                        }
                        try {
                          // 1er intento: filtrando por CardCode del leasing.
                          let resolved = await resolveLeasingInvoiceByDocNumApi(
                            bankCode,
                            contractNumber,
                            r.index,
                            docNum,
                          );
                          // 2º intento: si 0 resultados, buscar sin filtrar por
                          // CardCode (para casos donde la factura está registrada
                          // con un proveedor distinto al configurado en el leasing).
                          if (resolved.length === 0) {
                            const tryWide = confirm(
                              `No se encontró ninguna factura con Nº ${docNum} para el proveedor ` +
                                `del leasing. ¿Quieres buscarla en TODAS las facturas de SAP ` +
                                `(sin filtrar por proveedor)? Útil si la factura está registrada ` +
                                `en SAP con otro CardCode distinto al del leasing.`,
                            );
                            if (!tryWide) return;
                            resolved = await resolveLeasingInvoiceByDocNumApi(
                              bankCode,
                              contractNumber,
                              r.index,
                              docNum,
                              { anyCardCode: true },
                            );
                          }
                          if (resolved.length === 0) {
                            alert(
                              `No se encontró ninguna PurchaseInvoice con Nº ${docNum} en SAP. Verifica el número.`,
                            );
                            return;
                          }
                          if (resolved.length === 1) {
                            const c = resolved[0]!;
                            if (
                              !confirm(
                                `Encontrada factura Nº ${c.docNum} · ${c.docDate} · Total ${c.docTotal.toFixed(2)}€\n` +
                                  `Proveedor: ${c.cardCode} ${c.cardName ? `(${c.cardName})` : ""}\n\n¿Vincular a la cuota #${r.index}?`,
                              )
                            )
                              return;
                            newDoc = c.docEntry;
                          } else {
                            const list = resolved
                              .map(
                                (c, i) =>
                                  `${i + 1}. Nº ${c.docNum} · ${c.docDate} · ${c.cardCode} ${c.cardName ? `(${c.cardName})` : ""} · Total ${c.docTotal.toFixed(2)}€`,
                              )
                              .join("\n");
                            const p2 = prompt(
                              `Hay ${resolved.length} facturas con Nº ${docNum}:\n${list}\n\nEscribe 1-${resolved.length}:`,
                              "1",
                            );
                            if (!p2) return;
                            const n = parseInt(p2, 10);
                            if (!Number.isFinite(n) || n < 1 || n > resolved.length) {
                              alert("Selección inválida.");
                              return;
                            }
                            newDoc = resolved[n - 1]!.docEntry;
                          }
                        } catch (e: unknown) {
                          alert(e instanceof Error ? e.message : String(e));
                          return;
                        }
                      } else {
                        const n = parseInt(t, 10);
                        if (!Number.isFinite(n) || n < 1 || n > candidates.length) {
                          alert(
                            candidates.length > 0
                              ? `Selección inválida. Debe ser 1-${candidates.length}, F, D o X.`
                              : `No hay facturas en la lista. Usa F (por Nº), D (por DocEntry) o X (sin factura).`,
                          );
                          return;
                        }
                        newDoc = candidates[n - 1]!.docEntry;
                      }
                      try {
                        await setLeasingInstallmentInvoiceDocEntryApi(
                          bankCode,
                          contractNumber,
                          r.index,
                          newDoc,
                        );
                        await load();
                      } catch (e: unknown) {
                        alert(e instanceof Error ? e.message : String(e));
                      }
                    }}
                  />
                ))}
                {totals && (
                  <tr style={{ borderTop: "2px solid rgba(128,128,128,0.4)", fontWeight: 600 }}>
                    <td colSpan={2} className="meta" style={{ textAlign: "right" }}>Totales</td>
                    <td className="num">{formatMoney(totals.base, currency)}</td>
                    <td className="num">{formatMoney(totals.interest, currency)}</td>
                    <td className="num">{formatMoney(totals.amortized, currency)}</td>
                    <td className="num">{formatMoney(totals.vat, currency)}</td>
                    <td className="num">{totals.postalFee > 0 ? formatMoney(totals.postalFee, currency) : <span className="meta">—</span>}</td>
                    <td className="num">{formatMoney(totals.total, currency)}</td>
                    <td className="num">{formatMoney(totals.totalPayment, currency)}</td>
                    <td colSpan={2} />
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {data.generatedAt && (
            <p className="meta" style={{ fontSize: "0.8em", marginTop: 6 }}>
              Última generación: {new Date(data.generatedAt).toLocaleString()}
              {"  ·  "}Clic sobre Fecha / Base / Interés / Amortizado / Envío / Total para editar; IVA, Total pago y Pdte capital se recalculan en cascada.
            </p>
          )}
        </>
      )}

      {pdfPreview && (
        <PdfPreviewModal
          preview={pdfPreview.preview}
          currency={currency}
          busy={busy}
          onCancel={() => setPdfPreview(null)}
          onConfirm={() => void confirmImportPdf()}
        />
      )}
    </section>
  );
}

function PdfPreviewModal({
  preview,
  currency,
  busy,
  onCancel,
  onConfirm,
}: {
  preview: LeasingPdfPreview;
  currency: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const nRows = preview.rowsCount;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 40,
      }}
      onClick={onCancel}
    >
      <div
        className="card"
        style={{ maxWidth: 1000, width: "95%", maxHeight: "92vh", overflow: "auto" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="card-head">
          <h3 style={{ margin: 0 }}>Preview de importación de PDF</h3>
          <button type="button" onClick={onCancel} disabled={busy}>Cerrar</button>
        </div>
        <p className="meta" style={{ fontSize: "0.85em" }}>
          Archivo: <code>{preview.fileName ?? "(sin nombre)"}</code>
          {"  ·  "}Texto extraído: <strong>{preview.extractedTextLength}</strong> caracteres
          {"  ·  "}Filas detectadas: <strong>{nRows}</strong>
        </p>

        {nRows === 0 ? (
          <p className="error">
            No se detectó ninguna fila. Copia el texto de abajo y pásamelo para afinar el parser.
          </p>
        ) : (
          <>
            <table className="data" style={{ fontSize: "0.8em", marginTop: 8 }}>
              <thead>
                <tr>
                  <th className="num">Nº</th>
                  <th>Fecha</th>
                  <th className="num">Base</th>
                  <th className="num">Interés</th>
                  <th className="num">Amort.</th>
                  <th className="num">IVA</th>
                  <th className="num">Envío</th>
                  <th className="num">Total</th>
                  <th className="num">Total pago</th>
                  <th className="num">Pdte cap.</th>
                  <th>Res</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, 6).map((r) => (
                  <tr key={r.index}>
                    <td className="num">{r.index}</td>
                    <td>{r.date || "—"}</td>
                    <td className="num">{formatMoney(r.base, currency)}</td>
                    <td className="num">{formatMoney(r.interest, currency)}</td>
                    <td className="num">{formatMoney(r.amortized, currency)}</td>
                    <td className="num">{formatMoney(r.vat, currency)}</td>
                    <td className="num">{r.postalFee ? formatMoney(r.postalFee, currency) : "—"}</td>
                    <td className="num">{formatMoney(r.total, currency)}</td>
                    <td className="num">{formatMoney(r.totalPayment, currency)}</td>
                    <td className="num">{formatMoney(r.pendingCapital, currency)}</td>
                    <td>{r.isResidual ? "VR" : ""}</td>
                  </tr>
                ))}
                {nRows > 6 && (
                  <tr>
                    <td colSpan={11} className="meta" style={{ textAlign: "center" }}>
                      … y {nRows - 6} filas más (últimas: {preview.rows.slice(-2).map((r) => r.index).join(", ")})
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}

        <details style={{ marginTop: 12 }}>
          <summary style={{ cursor: "pointer" }}>
            Ver texto extraído del PDF (para diagnóstico)
          </summary>
          <textarea
            value={preview.extractedTextSample}
            readOnly
            rows={16}
            style={{ width: "100%", fontFamily: "monospace", fontSize: "0.8em", marginTop: 6 }}
          />
        </details>

        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end", marginTop: 12 }}>
          <button type="button" onClick={onCancel} disabled={busy}>Cancelar</button>
          <button type="button" onClick={onConfirm} disabled={busy || nRows === 0}>
            {busy ? "Guardando…" : `Guardar ${nRows} cuotas`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Fila del cuadro con celdas editables ───────────────────────────────────

function ScheduleRow({
  row,
  currency,
  onEditCell,
  onToggleManualMark,
  onReopenAsPartial,
  onEditInvoiceDocEntry,
}: {
  row: LeasingInstallment;
  currency: string;
  onEditCell: (atIndex: number, field: LeasingScheduleEditableField, value: number | string) => Promise<void> | void;
  onToggleManualMark: () => void | Promise<void>;
  onReopenAsPartial: () => void | Promise<void>;
  onEditInvoiceDocEntry: () => void | Promise<void>;
}) {
  const canEdit = !row.paidAt && row.status !== "partial";
  const isPartial = row.status === "partial";
  return (
    <tr
      style={{
        background: row.paidAt
          ? "rgba(46,125,50,0.10)"
          : isPartial
            ? "rgba(255,165,0,0.12)"
            : row.isResidual
              ? "rgba(255,193,7,0.10)"
              : undefined,
        fontSize: "0.9em",
      }}
      title={
        isPartial
          ? `Cuota PARCIALMENTE pagada: ${formatMoney(row.paidAmount ?? 0, currency)} de ${formatMoney(row.totalPayment ?? row.total, currency)}. Factura DocEntry=${row.invoiceDocEntry ?? "?"}. Pendiente hasta que llegue el cargo residual.`
          : row.isResidual
            ? "Cuota de valor residual (solo se puede editar la fecha)"
            : undefined
      }
    >
      <td className="num">
        {row.index}
        {row.isResidual && <span className="meta" style={{ marginLeft: 4, fontSize: "0.85em" }}>· residual</span>}
      </td>
      <EditableDateCell
        value={row.date}
        disabled={!canEdit}
        onCommit={(v) => onEditCell(row.index, "date", v)}
      />
      <EditableNumCell
        value={row.base}
        currency={currency}
        disabled={!canEdit || row.isResidual}
        hint="Clic para editar la base. Recalcula cuotas siguientes."
        onCommit={(v) => onEditCell(row.index, "base", v)}
      />
      <EditableNumCell
        value={row.interest}
        currency={currency}
        disabled={!canEdit || row.isResidual}
        hint="Clic para forzar el interés. Amortizado = base − interés. Cascada al resto."
        onCommit={(v) => onEditCell(row.index, "interest", v)}
      />
      <EditableNumCell
        value={row.amortized}
        currency={currency}
        disabled={!canEdit || row.isResidual}
        hint="Clic para forzar el amortizado. Interés = base − amortizado. Cascada al resto."
        onCommit={(v) => onEditCell(row.index, "amortized", v)}
      />
      <td className="num">{formatMoney(row.vat, currency)}</td>
      <EditableNumCell
        value={row.postalFee ?? 0}
        currency={currency}
        disabled={!canEdit || row.isResidual}
        hint="Clic para editar el envío. Aplica a esta cuota y siguientes."
        onCommit={(v) => onEditCell(row.index, "postalFee", v)}
        showDashOnZero
      />
      <EditableNumCell
        value={row.total}
        currency={currency}
        disabled={!canEdit || row.isResidual}
        strong
        hint="Total factura = base + IVA. Clic para editar; recalcula base y cascada."
        onCommit={(v) => onEditCell(row.index, "total", v)}
      />
      <td className="num" style={{ fontWeight: 600 }} title="Total pago = base + envío + IVA (lo que se carga en cuenta)">
        {formatMoney(row.totalPayment ?? row.total, currency)}
      </td>
      <td className="num">{formatMoney(row.pendingCapital, currency)}</td>
      <td>
        {isPartial ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
            <span
              style={{
                background: "rgba(255,165,0,0.18)",
                border: "1px solid rgba(255,140,0,0.5)",
                borderRadius: 4,
                padding: "1px 6px",
                fontSize: "0.75em",
                display: "inline-block",
              }}
              title={`Pagado ${formatMoney(row.paidAmount ?? 0, currency)} de ${formatMoney(row.totalPayment ?? row.total, currency)}. Espera cargo residual.`}
            >
              PARCIAL {formatMoney(row.paidAmount ?? 0, currency)}
              {" / "}
              {formatMoney(row.totalPayment ?? row.total, currency)}
            </span>
            <span className="meta" style={{ fontSize: "0.7em", display: "inline-flex", alignItems: "center", gap: 4 }}>
              Factura vinculada:{" "}
              {row.invoiceDocEntry != null ? (
                <code>ID interno {row.invoiceDocEntry}</code>
              ) : (
                <em>sin vincular</em>
              )}
              <button
                type="button"
                onClick={() => void onEditInvoiceDocEntry()}
                title="Cambiar la factura vinculada (elige de la lista de facturas del proveedor en SAP)"
                style={{
                  padding: "1px 5px",
                  fontSize: "0.85em",
                  cursor: "pointer",
                  border: "1px solid rgba(128,128,128,0.4)",
                  background: "transparent",
                  borderRadius: 3,
                }}
              >
                ✎ cambiar
              </button>
            </span>
          </div>
        ) : row.paidAt ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 2, alignItems: "flex-start" }}>
            <span
              className="badge ok"
              title={row.paidJdtNum ? `JdtNum ${row.paidJdtNum}` : row.manuallyMarked ? "Marcada manualmente (implantación)" : "pagada"}
            >
              ✓ {formatDateEs(row.paidAt.slice(0, 10))}
              {row.manuallyMarked ? " (man.)" : row.paidJdtNum ? "" : ""}
            </span>
            {row.manuallyMarked && !row.paidJdtNum && (
              <button
                type="button"
                onClick={onToggleManualMark}
                style={{ fontSize: "0.7em", padding: "1px 6px" }}
                title="Retirar marca manual"
              >
                Desmarcar
              </button>
            )}
            {!row.manuallyMarked && row.paidJdtNum && (
              <button
                type="button"
                onClick={onReopenAsPartial}
                style={{ fontSize: "0.7em", padding: "1px 6px" }}
                title="Reparar dato heredado: la cuota está marcada como pagada pero en SAP la factura solo se pagó parcialmente. Reabre como partial para poder recibir el cargo residual."
              >
                Reabrir parcial
              </button>
            )}
          </div>
        ) : (
          <button
            type="button"
            onClick={onToggleManualMark}
            style={{ fontSize: "0.75em", padding: "2px 8px" }}
            title="Marcar como contabilizado manualmente (fase de implantación)"
          >
            Marcar
          </button>
        )}
      </td>
    </tr>
  );
}

function EditableNumCell({
  value,
  currency,
  disabled,
  strong,
  hint,
  showDashOnZero,
  onCommit,
}: {
  value: number;
  currency: string;
  disabled: boolean;
  strong?: boolean;
  hint: string;
  showDashOnZero?: boolean;
  onCommit: (n: number) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState<string>(value.toFixed(2));

  const commit = () => {
    const n = Number(input.replace(",", "."));
    if (!Number.isFinite(n) || n < 0) {
      setInput(value.toFixed(2));
      setEditing(false);
      return;
    }
    if (Math.abs(n - value) < 0.005) {
      setEditing(false);
      return;
    }
    setEditing(false);
    onCommit(n);
  };

  return (
    <td className="num" style={disabled ? undefined : { cursor: "pointer" }} title={disabled ? undefined : hint}>
      {editing ? (
        <input
          type="number"
          step="0.01"
          value={input}
          autoFocus
          onChange={(e) => setInput(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setInput(value.toFixed(2));
              setEditing(false);
            }
          }}
          style={{ width: "6rem", textAlign: "right" }}
        />
      ) : (
        <span
          onClick={() => {
            if (!disabled) {
              setInput(value.toFixed(2));
              setEditing(true);
            }
          }}
        >
          {showDashOnZero && value === 0 ? (
            <span className="meta">—</span>
          ) : strong ? (
            <strong>{formatMoney(value, currency)}</strong>
          ) : (
            formatMoney(value, currency)
          )}
        </span>
      )}
    </td>
  );
}

function EditableDateCell({
  value,
  disabled,
  onCommit,
}: {
  value: string;
  disabled: boolean;
  onCommit: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [input, setInput] = useState<string>(value);

  const commit = () => {
    const trimmed = input.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
      setInput(value);
      setEditing(false);
      return;
    }
    if (trimmed === value) {
      setEditing(false);
      return;
    }
    setEditing(false);
    onCommit(trimmed);
  };

  return (
    <td style={disabled ? undefined : { cursor: "pointer" }} title={disabled ? undefined : "Clic para editar la fecha."}>
      {editing ? (
        <input
          type="date"
          value={input}
          autoFocus
          onChange={(e) => setInput(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setInput(value);
              setEditing(false);
            }
          }}
        />
      ) : (
        <span
          onClick={() => {
            if (!disabled) {
              setInput(value);
              setEditing(true);
            }
          }}
        >
          {formatDateEs(value)}
        </span>
      )}
    </td>
  );
}
